import { useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import './AuthPage.css';
import { AuthMethods } from './AuthMethods';
import { useLocation } from 'react-router-dom';
import leaderid from '../../assets/icons/leaderid.svg';
import tracksSvgUrl from '../../assets/icons/треки.svg';
import eyeclosed from '../../assets/icons/eyeclosed.svg';
import eyeopen from '../../assets/icons/eye.svg';
import { getLeaderIdAuthorizationUrl, register as registerRequest } from '../../api/auth';
import {
  mapApiError,
  validateEmail,
  validateLogin,
  validatePassword,
  validatePasswordConfirm,
  validateRegisterName
} from '../../utils/validators';

export function Register() {
  const [fullName, setFullName] = useState('');
  const [login, setLogin] = useState('');
  const [isLoginDirty, setIsLoginDirty] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    fullName: '',
    login: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLeaderIdSubmitting, setIsLeaderIdSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const stageRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    const SVG_NS = 'http://www.w3.org/2000/svg';
    const TRACKS_FALLBACK = [
      { y: 0, height: 506.25, fill: '#070731' },
      { y: 506, height: 506.25, fill: '#171717' },
      { y: 1013, height: 506.25, fill: '#4C56E6' },
      { y: 1519, height: 506.25, fill: '#F5F5F5' }
    ];

    const stageEl = stageRef.current;
    const layerEl = layerRef.current;
    const animations = [];

    function setStageBackground(tracks, totalHeight) {
      if (!stageEl) return;
      const c1 = tracks[0];
      const c2 = tracks[1];
      const c3 = tracks[2];
      const c4 = tracks[3];
      const p1 = ((c1.y + c1.height) / totalHeight) * 100;
      const p2 = ((c2.y + c2.height) / totalHeight) * 100;
      const p3 = ((c3.y + c3.height) / totalHeight) * 100;

      stageEl.style.background = `linear-gradient(
        to bottom,
        ${c1.fill} 0%,
        ${c1.fill} ${p1}%,
        ${c2.fill} ${p1}%,
        ${c2.fill} ${p2}%,
        ${c3.fill} ${p2}%,
        ${c3.fill} ${p3}%,
        ${c4.fill} ${p3}%,
        ${c4.fill} 100%
      )`;
    }

    function extractTracks(svg) {
      const rects = [...svg.querySelectorAll('rect')]
        .map((r) => ({
          el: r,
          x: Number(r.getAttribute('x') || 0),
          y: Number(r.getAttribute('y') || 0),
          w: Number(r.getAttribute('width') || 0),
          h: Number(r.getAttribute('height') || 0),
          fill: r.getAttribute('fill') || 'transparent'
        }))
        .filter((r) => r.w >= 3500 && r.h >= 350 && r.h < 1000)
        .sort((a, b) => a.y - b.y)
        .slice(0, 4)
        .map((r) => ({ y: r.y, height: r.h, fill: r.fill }));

      return rects.length === 4 ? rects : TRACKS_FALLBACK;
    }

    function isBackgroundRect(el) {
      const w = Number(el.getAttribute('width') || 0);
      const h = Number(el.getAttribute('height') || 0);
      return w >= 3500 && h >= 350;
    }

    function assignTrackByY(yCenter, tracks) {
      for (let i = 0; i < tracks.length; i += 1) {
        const t = tracks[i];
        if (yCenter >= t.y && yCenter < t.y + t.height) return i;
      }
      return 0;
    }

    function animateTrackGroup(group, trackIndex, totalWidth) {
      const toLeft = trackIndex % 2 === 0;
      const dir = toLeft ? -1 : 1;
      const duration = 22000;

      const content = document.createElementNS(SVG_NS, 'g');
      while (group.firstChild) content.appendChild(group.firstChild);

      const copy = content.cloneNode(true);
      const offset = toLeft ? totalWidth : -totalWidth;
      copy.setAttribute('transform', `translate(${offset} 0)`);

      group.appendChild(content);
      group.appendChild(copy);

      const anim = group.animate(
        [
          { transform: 'translateX(0px)' },
          { transform: `translateX(${dir * totalWidth}px)` }
        ],
        {
          duration,
          iterations: Infinity,
          easing: 'linear'
        }
      );

      return anim;
    }

    async function init() {
      if (!stageEl || !layerEl) return;
      setStageBackground(TRACKS_FALLBACK, 2025);

      try {
        const res = await fetch(tracksSvgUrl);
        if (!res.ok) return;
        const text = await res.text();

        const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
        const srcSvg = doc.documentElement;
        if (!srcSvg || srcSvg.tagName.toLowerCase() !== 'svg') return;

        const vb = (srcSvg.getAttribute('viewBox') || '0 0 3600 2025').split(/\s+/).map(Number);
        const totalW = vb[2] || 3600;
        const totalH = vb[3] || 2025;

        layerEl.setAttribute('viewBox', `0 0 ${totalW} ${totalH}`);

        const tracks = extractTracks(srcSvg);
        setStageBackground(tracks, totalH);

        const groups = tracks.map((t, idx) => {
          const g = document.createElementNS(SVG_NS, 'g');
          g.setAttribute('data-track', String(idx + 1));
          layerEl.appendChild(g);
          return g;
        });

        const measureHost = document.createElement('div');
        measureHost.style.position = 'fixed';
        measureHost.style.left = '-99999px';
        measureHost.style.top = '-99999px';
        measureHost.style.width = '1px';
        measureHost.style.height = '1px';
        measureHost.style.overflow = 'hidden';
        measureHost.style.pointerEvents = 'none';
        measureHost.style.opacity = '0';

        const measureSvg = srcSvg.cloneNode(true);
        measureHost.appendChild(measureSvg);
        document.body.appendChild(measureHost);

        const sourceItems = [...measureSvg.querySelectorAll('path, rect')];

        sourceItems.forEach((el) => {
          if (el.tagName.toLowerCase() === 'rect' && isBackgroundRect(el)) return;

          let box;
          try {
            box = el.getBBox();
          } catch {
            return;
          }

          if (!box || !Number.isFinite(box.y)) return;
          const yCenter = box.y + box.height / 2;
          const idx = assignTrackByY(yCenter, tracks);

          if (
            idx === 0 &&
            box.y < 150 &&
            box.x > 1600 &&
            box.x < 2050 &&
            box.height < 40
          ) {
            return;
          }

          groups[idx].appendChild(el.cloneNode(true));
        });

        measureHost.remove();

        groups.forEach((g, i) => {
          const a = animateTrackGroup(g, i, totalW);
          if (a) animations.push(a);
        });
      } catch {
        // ignore animation asset parsing errors
      }
    }

    init();

    return () => {
      animations.forEach((a) => {
        try {
          a.cancel();
        } catch {}
      });
      if (layerEl) {
        while (layerEl.firstChild) layerEl.removeChild(layerEl.firstChild);
      }
    };
    // eslint-disable-next-line
  }, [tracksSvgUrl]);

  useEffect(() => {
    if (isLoginDirty) return;
    const suggestedLogin = String(fullName || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[_\-.]+|[_\-.]+$/g, '')
      .slice(0, 50);
    setLogin(suggestedLogin);
  }, [fullName, isLoginDirty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const nextFieldErrors = {
      fullName: validateRegisterName(fullName),
      login: validateLogin(login),
      email: validateEmail(email),
      password: validatePassword(password),
      confirmPassword: validatePasswordConfirm(password, confirmPassword)
    };
    setFieldErrors(nextFieldErrors);

    if (
      nextFieldErrors.fullName ||
      nextFieldErrors.login ||
      nextFieldErrors.email ||
      nextFieldErrors.password ||
      nextFieldErrors.confirmPassword
    ) {
      return;
    }

    const normalizedFullName = fullName.trim().replace(/\s+/g, ' ');
    const nameParts = normalizedFullName.split(' ').filter(Boolean);
    const loginName = login.trim();
    const lastName = nameParts[0] || '';
    const firstName = nameParts[1] || '';
    const patronymic = nameParts.length > 2 ? nameParts.slice(2).join(' ') : null;

    if (!firstName || !lastName) {
      setError('Укажи минимум фамилию и имя.');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await registerRequest(
        loginName,
        email,
        password,
        firstName,
        lastName,
        patronymic
      );
      navigate('/login', {
        replace: true,
        state: {
          email: email.trim(),
          infoMessage:
            response?.detail ||
            'Регистрация принята. Дождитесь письма на почту, подтвердите email и затем войдите.'
        }
      });
    } catch (err) {
      setError(mapApiError('register', err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeaderIdAuth = async () => {
    try {
      setError('');
      setIsLeaderIdSubmitting(true);
      const payload = await getLeaderIdAuthorizationUrl();
      if (!payload?.authorization_url) {
        throw new Error('Не удалось получить ссылку авторизации Leader-ID.');
      }
      window.location.assign(payload.authorization_url);
    } catch (err) {
      setError(err.message || 'Не удалось начать авторизацию через Leader-ID.');
      setIsLeaderIdSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-image">
        <main
          className="stage"
          ref={stageRef}
          aria-label="Animated tracks banner"
          style={{ width: '100%', height: '100%', minHeight: '200px' }}
        >
          <svg
            id="objectsLayer"
            ref={layerRef}
            className="objects-layer"
            viewBox="0 0 3600 2025"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            style={{ width: '100%', height: '100%' }}
          ></svg>
        </main>
      </div>
      <div className="auth-form-container">
        <div className="auth-card">
          <h1 className="auth-title auth-title-primary">Добро пожаловать!</h1>
          <AuthMethods registration forceAlternative={new URLSearchParams(location.search).get('method') === 'email'}>
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="register-fullname">
                Ваше ФИО
              </label>
              <input
                className="form-input"
                id="register-fullname"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, fullName: '' }));
                }}
                onBlur={() =>
                  setFieldErrors((prev) => ({ ...prev, fullName: validateRegisterName(fullName) }))
                }
                placeholder="Введите ваше ФИО"
                required
              />
              {fieldErrors.fullName ? <p className="form-error">{fieldErrors.fullName}</p> : null}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="register-email">
                Введите вашу почту
              </label>
              <input
                className="form-input"
                id="register-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, email: '' }));
                }}
                onBlur={() =>
                  setFieldErrors((prev) => ({ ...prev, email: validateEmail(email) }))
                }
                placeholder="Введите вашу почту"
                required
              />
              {fieldErrors.email ? <p className="form-error">{fieldErrors.email}</p> : null}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="register-login">
                Придумайте логин
              </label>
              <input
                className="form-input"
                id="register-login"
                value={login}
                onChange={(e) => {
                  setLogin(e.target.value);
                  setIsLoginDirty(true);
                  setFieldErrors((prev) => ({ ...prev, login: '' }));
                }}
                onBlur={() =>
                  setFieldErrors((prev) => ({ ...prev, login: validateLogin(login) }))
                }
                placeholder="Например: vadim.voloshchuk"
                required
              />
              {fieldErrors.login ? <p className="form-error">{fieldErrors.login}</p> : null}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="register-password">
                Придумайте пароль
              </label>
              <div className="password-input-wrapper">
                <input
                  className="form-input"
                  id="register-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  onBlur={() =>
                    setFieldErrors((prev) => ({ ...prev, password: validatePassword(password) }))
                  }
                  placeholder="Придумайте пароль"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <img src={eyeopen} alt="" />
                  ) : (
                    <img src={eyeclosed} alt="" />
                  )}
                </button>
              </div>
              {fieldErrors.password ? <p className="form-error">{fieldErrors.password}</p> : null}
              <p className="passwordRequirements">Пароль должен содержать заглавную букву и специальный символ</p>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="register-confirm-password">
                Повторите пароль
              </label>
              <div className="password-input-wrapper">
                <input
                  className="form-input"
                  id="register-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, confirmPassword: '' }));
                  }}
                  onBlur={() =>
                    setFieldErrors((prev) => ({
                      ...prev,
                      confirmPassword: validatePasswordConfirm(password, confirmPassword)
                    }))
                  }
                  placeholder="Повторите пароль"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <img src={eyeopen} alt="" />
                  ) : (
                    <img src={eyeclosed} alt="" />
                  )}
                </button>
              </div>
              {fieldErrors.confirmPassword ? <p className="form-error">{fieldErrors.confirmPassword}</p> : null}
            </div>
            <button type="submit" className="auth-button" disabled={isSubmitting}>
              {isSubmitting ? 'Регистрируем...' : 'Зарегистрироваться'}
            </button>
          </form>
          {error && <p className="auth-link">{error}</p>}
          <p className="auth-link">
            Уже есть аккаунт? <Link to="/login" className="auth-link-register">Авторизоваться</Link>
          </p>
          <p className="auth-support-note">
            По всем техническим доработкам и предложениям пишите на sfedupro@yandex.com.
          </p>
          <button
            type="button"
            className="auth-leaderid"
            onClick={handleLeaderIdAuth}
            disabled={isLeaderIdSubmitting}
          >
            <img src={leaderid} alt="" />
            {isLeaderIdSubmitting ? 'Переходим в Leader-ID...' : 'Войти с помощью Leader-ID'}
          </button>
          </AuthMethods>
        </div>
      </div>
    </div>
  );
}
